<template>
  <div class="row">
    <div class="col-12 grid-margin stretch-card">
      <div class="card">
        <div class="card-body">
          <h4 class="card-title">RentCast Property Synchronization</h4>
          <p class="card-description">
            Fetch properties from RentCast to preview them, then select which ones to import into your database.
            Please use this feature mindfully to respect the API rate limits of the Foundation plan.
          </p>
          
          <div v-if="alertMessage" :class="['alert', alertType === 'success' ? 'alert-success' : 'alert-danger']" role="alert">
            {{ alertMessage }}
          </div>

          <div class="form-group mt-4">
             <label>Select Sync Region (Optional)</label>
             <select class="form-control" v-model="selectedState">
                <option value="">Entire United States (All states)</option>
                <option value="CA">California</option>
                <option value="TX">Texas</option>
                <option value="FL">Florida</option>
                <option value="NY">New York</option>
                <!-- Add more as needed -->
             </select>
             <small class="form-text text-muted">Leave empty to fetch across the entire US. Selecting a state helps reduce API usage.</small>
          </div>

          <button 
            type="button" 
            class="btn btn-primary mr-2" 
            @click="fetchProperties" 
            :disabled="isFetching"
          >
            <span v-if="isFetching" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            {{ isFetching ? 'Fetching...' : 'Fetch Properties Preview' }}
          </button>

          <!-- Properties Preview Table -->
          <div v-if="fetchedProperties.length > 0" class="mt-5">
            <h5 class="mb-3">Preview Fetched Properties</h5>
            <div class="table-responsive">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th>
                      <input type="checkbox" @change="toggleAll" :checked="selectedProperties.length === fetchedProperties.length" />
                    </th>
                    <th>Address</th>
                    <th>City</th>
                    <th>State</th>
                    <th>Property Type</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(property, index) in fetchedProperties" :key="index">
                    <td>
                      <input type="checkbox" :value="property" v-model="selectedProperties" />
                    </td>
                    <td>{{ property.addressLine1 }}</td>
                    <td>{{ property.city }}</td>
                    <td>{{ property.state }}</td>
                    <td>{{ property.propertyType }}</td>
                    <td>{{ property.price ? '$' + property.price : 'N/A' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-3">
              <button 
                type="button" 
                class="btn btn-success" 
                @click="importSelected" 
                :disabled="isImporting || selectedProperties.length === 0"
              >
                <span v-if="isImporting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                {{ isImporting ? 'Importing...' : `Import Selected (${selectedProperties.length})` }}
              </button>
            </div>
          </div>
          <div v-else-if="hasFetched" class="mt-5">
            <p class="text-muted">No properties found for the selected region.</p>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/api";

export default {
  name: "RentcastSync",
  data() {
    return {
      isFetching: false,
      isImporting: false,
      hasFetched: false,
      alertMessage: "",
      alertType: "",
      selectedState: "",
      fetchedProperties: [],
      selectedProperties: [],
    };
  },
  methods: {
    fetchProperties() {
      this.isFetching = true;
      this.hasFetched = false;
      this.alertMessage = "";
      this.fetchedProperties = [];
      this.selectedProperties = [];
      
      const payload = {
        state: this.selectedState
      };

      API.fetchRentcastProperties(
        payload,
        (response) => {
          this.isFetching = false;
          this.hasFetched = true;
          // Assuming backend returns an array of properties in response.data.properties
          this.fetchedProperties = response.data?.properties || [];
          if(this.fetchedProperties.length > 0) {
             this.alertType = "success";
             this.alertMessage = `Successfully fetched ${this.fetchedProperties.length} properties. Please select the ones you want to import.`;
          }
        },
        (error) => {
          this.isFetching = false;
          this.alertType = "error";
          this.alertMessage = error.response?.data?.message || "An error occurred during fetching. Please try again.";
        }
      );
    },
    toggleAll(event) {
      if (event.target.checked) {
        this.selectedProperties = [...this.fetchedProperties];
      } else {
        this.selectedProperties = [];
      }
    },
    importSelected() {
      if (this.selectedProperties.length === 0) return;
      
      this.isImporting = true;
      this.alertMessage = "";

      const payload = {
        properties: this.selectedProperties
      };

      API.importRentcastProperties(
        payload,
        (response) => {
          this.isImporting = false;
          this.alertType = "success";
          this.alertMessage = response.data?.message || `Successfully imported ${this.selectedProperties.length} properties!`;
          // Clear selection after successful import
          this.selectedProperties = [];
        },
        (error) => {
          this.isImporting = false;
          this.alertType = "error";
          this.alertMessage = error.response?.data?.message || "An error occurred during import. Please try again.";
        }
      );
    }
  },
};
</script>

<style scoped>
.spinner-border {
  margin-right: 5px;
}
.table-responsive {
  max-height: 400px;
  overflow-y: auto;
}
</style>
